import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { Button } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DecimalPipe } from '@angular/common';
import { GridStack, GridStackNode, GridStackOptions } from 'gridstack';
import {
  CustomGridStackWidget,
  GridWidgetConfigService,
} from '../../../services/grid-widget-config.service';
import { WidgetArrangePlaceholderComponent } from '../../molecules/widget-arrange-placeholder/widget-arrange-placeholder.component';
import { HeaderComponent } from '../../atoms/header/header.component';
import { SearchInputComponent } from '../../atoms/search-input/search-input.component';

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 1.6;
const ZOOM_STEP = 0.1;
const LABEL_VISIBLE_ZOOM_THRESHOLD = 0.7;

@Component({
  selector: 'app-arrange-grid-dialog',
  standalone: true,
  imports: [
    DialogModule,
    Button,
    TooltipModule,
    DecimalPipe,
    WidgetArrangePlaceholderComponent,
    HeaderComponent,
    SearchInputComponent,
  ],
  templateUrl: './arrange-grid-dialog.component.html',
  styleUrl: './arrange-grid-dialog.component.scss',
})
export class ArrangeGridDialogComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() layout: CustomGridStackWidget[] = [];
  @Input() cellAspectRatio = 1;
  @Output() apply = new EventEmitter<CustomGridStackWidget[]>();
  @Output() cancel = new EventEmitter<void>();

  @ViewChild('miniGrid') miniGridRef?: ElementRef<HTMLElement>;

  items: CustomGridStackWidget[] = [];
  zoomLevel = 1;
  searchQuery = '';
  selectedTileId: string | null = null;

  private grid?: GridStack;
  private history: CustomGridStackWidget[][] = [];
  private suppressAutoHistory = false;

  constructor(private configService: GridWidgetConfigService) {}

  /**
   * Snapshots the incoming layout into a local working copy whenever the dialog opens,
   * and tears down the mini-grid again once it closes. Working on a local copy means
   * dragging/resizing inside the dialog never touches the real grid until "Übernehmen".
   *
   * @param {SimpleChanges} changes - Angular input change record.
   * @return {void} Does not return a value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']) {
      if (this.visible) {
        this.items = structuredClone(this.layout);
        this.history = [];
        this.zoomLevel = 1;
        this.searchQuery = '';
        this.selectedTileId = null;
        setTimeout(() => this.initMiniGrid(), 50);
      } else {
        this.destroyMiniGrid();
      }
    }
  }

  private initMiniGrid(): void {
    if (!this.miniGridRef || !this.visible) return;
    const options: GridStackOptions = {
      column: 12,
      cellHeight: '2.25rem',
      margin: 5,
      float: true,
      resizable: { handles: 'e,se,s,sw,w' },
      acceptWidgets: false,
      removable: false,
    };
    this.grid = GridStack.init(options, this.miniGridRef.nativeElement);
    this.applyCellAspectRatio();
    this.grid.on('change', (event: Event, changedNodes: GridStackNode[]) => {
      if (!this.suppressAutoHistory) {
        this.pushHistory();
      }
      this.syncItemsFromNodes(changedNodes);
    });
  }

  /**
   * Merges gridstack's reported node positions/sizes back into the local `items`
   * mirror. Shared by the drag/resize 'change' event, the compact button, and
   * the arrow-key nudge — so all three ways of moving a tile stay consistent.
   *
   * @param {GridStackNode[]} nodes - Nodes reported by gridstack.
   * @return {void} Does not return a value.
   */
  private syncItemsFromNodes(nodes: GridStackNode[]): void {
    nodes.forEach(node => {
      const item = this.items.find(i => String(i.id) === String(node.id));
      if (item) {
        item.x = node.x;
        item.y = node.y;
        item.w = node.w;
        item.h = node.h;
      }
    });
  }

  /**
   * Finds the live gridstack node for a given tile id, coercing both sides to
   * string — gridstack always reads `gs-id` back off the DOM as a string, but
   * our ids can be numbers (see the `undo()` doc comment for the full story).
   *
   * @param {string | number} id - The tile id to look up.
   * @return {GridStackNode | undefined} The matching node, if any.
   */
  private findNode(id: string | number): GridStackNode | undefined {
    return this.grid?.engine.nodes.find(n => String(n.id) === String(id));
  }

  /**
   * Runs a programmatic grid mutation (compact, undo-restore, arrow-key nudge)
   * without letting the shared 'change' listener push a second, redundant
   * history entry for it — the caller has already pushed one beforehand.
   *
   * @param {() => void} fn - The gridstack mutation to run.
   * @return {void} Does not return a value.
   */
  private withoutHistoryTracking(fn: () => void): void {
    this.suppressAutoHistory = true;
    try {
      fn();
    } finally {
      this.suppressAutoHistory = false;
    }
  }

  private pushHistory(): void {
    this.history.push(structuredClone(this.items));
  }

  get canUndo(): boolean {
    return this.history.length > 0;
  }

  /**
   * Reverts the last drag/resize/compact/nudge by restoring the previous snapshot
   * and re-applying its positions to the existing DOM nodes via `grid.update()`.
   *
   * Deliberately NOT using gridstack's `load()` here: it matches items to nodes
   * via strict `===` on `id`, but our ids can be numbers while gridstack always
   * reads `gs-id` back off the DOM as a string — the mismatch makes `load()`
   * think every tile is "new", so it deletes the existing (Angular-rendered)
   * elements and replaces them with bare, content-less gridstack placeholders.
   * `update()` instead targets the node's real `el` directly, no id-matching
   * involved, so the Angular-rendered tile content stays intact.
   *
   * The whole restore runs inside `batchUpdate()`/`batchUpdate(false)`: gridstack
   * temporarily allows nodes to overlap while batching (no live collision
   * push-around), then resolves everything in one atomic pass at the end. Without
   * this, restoring tile A into a cell still occupied by not-yet-restored tile B
   * would make gridstack shove B to an arbitrary free cell mid-replay, so a
   * multi-tile undo could silently settle on a layout that doesn't match the
   * snapshot for 3+ tile rotations/swaps.
   *
   * @return {void} Does not return a value.
   */
  undo(): void {
    const previous = this.history.pop();
    if (!previous || !this.grid) return;
    this.withoutHistoryTracking(() => {
      this.grid!.batchUpdate();
      previous.forEach(item => {
        const node = this.findNode(item.id);
        if (node?.el) {
          this.grid!.update(node.el, {
            x: item.x,
            y: item.y,
            w: item.w,
            h: item.h,
          });
        }
      });
      this.grid!.batchUpdate(false);
    });
    this.items = previous;
    this.syncItemsFromNodes(this.grid.engine.nodes);
  }

  /**
   * Removes gaps in the mini-grid layout via gridstack's built-in compaction,
   * then re-syncs the local `items` mirror as a safety net.
   *
   * @return {void} Does not return a value.
   */
  compactLayout(): void {
    if (!this.grid) return;
    this.pushHistory();
    this.withoutHistoryTracking(() => this.grid!.compact());
    this.syncItemsFromNodes(this.grid.engine.nodes);
  }

  zoomIn(): void {
    this.setZoom(this.zoomLevel + ZOOM_STEP);
  }

  zoomOut(): void {
    this.setZoom(this.zoomLevel - ZOOM_STEP);
  }

  resetZoom(): void {
    this.setZoom(1);
  }

  onWheelZoom(event: WheelEvent): void {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    this.setZoom(this.zoomLevel + (event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
  }

  /**
   * Applies a new zoom level by resizing the mini-grid's own container width
   * (never via CSS transform — that would throw off gridstack's drag/resize
   * coordinate math). Written directly to the DOM so the width takes effect
   * synchronously, letting `applyCellAspectRatio()` measure it immediately after.
   *
   * @param {number} value - The requested zoom level (clamped to [MIN_ZOOM, MAX_ZOOM]).
   * @return {void} Does not return a value.
   */
  private setZoom(value: number): void {
    const snapped = Math.round(value / ZOOM_STEP) * ZOOM_STEP;
    this.zoomLevel = Math.min(
      MAX_ZOOM,
      Math.max(MIN_ZOOM, Math.round(snapped * 100) / 100)
    );
    if (this.miniGridRef) {
      this.miniGridRef.nativeElement.style.width = `${this.zoomLevel * 100}%`;
    }
    this.applyCellAspectRatio();
  }

  /**
   * Corrects the mini-grid's cell height so tiles keep the same width-to-height
   * ratio as the real grid, just scaled down to the dialog's narrower column width.
   *
   * @return {void} Does not return a value.
   */
  private applyCellAspectRatio(): void {
    if (!this.grid) return;
    const columnWidth = this.grid.cellWidth();
    if (!columnWidth) return;
    const height = Math.max(columnWidth * this.cellAspectRatio, 22);
    this.grid.cellHeight(height, true);
  }

  private destroyMiniGrid(): void {
    this.grid?.destroy(true);
    this.grid = undefined;
  }

  selectTile(id: string | number): void {
    this.selectedTileId = String(id);
  }

  deselectTile(): void {
    this.selectedTileId = null;
  }

  isSelected(id: string | number): boolean {
    return this.selectedTileId === String(id);
  }

  /**
   * Below a certain zoom level tiles get too small to read their label — hide
   * it and rely on the tile's `aria-label`/tooltip (see `getTileLabel()`) instead.
   *
   * @return {boolean} Whether tile labels should render inline.
   */
  get showTileLabels(): boolean {
    return this.zoomLevel >= LABEL_VISIBLE_ZOOM_THRESHOLD;
  }

  /**
   * Resolves the display text for a tile: its headline, falling back to the
   * widget type's label. Used as the tile's `aria-label` (always, so its name
   * stays available to assistive tech regardless of zoom) and as the tooltip
   * text shown when the inline label is hidden at low zoom levels, and as the
   * text matched against the search filter.
   *
   * @param {CustomGridStackWidget} item - The tile to resolve text for.
   * @return {string} The headline, falling back to the widget type's label.
   */
  getTileLabel(item: CustomGridStackWidget): string {
    return (
      item.widget?.headline ||
      this.configService.getTypeDisplay(item.type)?.label ||
      ''
    );
  }

  /**
   * Determines whether a tile matches the current search filter. Non-matching
   * tiles stay in place and remain draggable — they're only dimmed, never
   * removed, since hiding them would break the grid's position math.
   *
   * @param {CustomGridStackWidget} item - The tile to check.
   * @return {boolean} True if the tile matches (or the search box is empty).
   */
  matchesSearch(item: CustomGridStackWidget): boolean {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) return true;
    return this.getTileLabel(item).toLowerCase().includes(query);
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.visible) return;
    const target = event.target as HTMLElement;
    const isTyping =
      !!target &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable);
    if (isTyping) return;

    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
      event.preventDefault();
      this.undo();
      return;
    }
    if (event.key === 'Enter') {
      // A focused button/link already handles Enter itself (native click) —
      // stepping in here too would silently override it, e.g. "Abbrechen"
      // having focus would end up calling onApply() instead of onCancel().
      const isInteractiveControl = !!target?.closest(
        'button, a, [role="button"]'
      );
      if (!isInteractiveControl) {
        event.preventDefault();
        this.onApply();
      }
      return;
    }
    if (
      this.selectedTileId &&
      ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)
    ) {
      event.preventDefault();
      this.moveSelectedTile(event.key);
    }
  }

  private moveSelectedTile(key: string): void {
    if (!this.grid || !this.selectedTileId) return;
    const node = this.findNode(this.selectedTileId);
    if (!node || !node.el) return;

    let x = node.x ?? 0;
    let y = node.y ?? 0;
    const w = node.w ?? 1;
    const column = this.grid.getColumn();
    switch (key) {
      case 'ArrowUp':
        y = Math.max(0, y - 1);
        break;
      case 'ArrowDown':
        y = y + 1;
        break;
      case 'ArrowLeft':
        x = Math.max(0, x - 1);
        break;
      case 'ArrowRight':
        x = Math.min(column - w, x + 1);
        break;
    }
    if (x === node.x && y === node.y) return; // at the boundary — nothing to do, don't spam undo history

    this.pushHistory();
    this.withoutHistoryTracking(() => this.grid!.update(node.el!, { x, y }));
    this.syncItemsFromNodes(this.grid.engine.nodes);
  }

  /**
   * Confirms the rearranged layout, handing the updated positions back to the caller.
   *
   * @return {void} Does not return a value.
   */
  onApply(): void {
    this.apply.emit(this.items);
    this.destroyMiniGrid();
  }

  /**
   * Discards the local working copy without affecting the real grid — also used
   * as the dialog's onHide handler (X button, Escape, backdrop click).
   *
   * @return {void} Does not return a value.
   */
  onCancel(): void {
    this.cancel.emit();
    this.destroyMiniGrid();
  }

  ngOnDestroy(): void {
    this.destroyMiniGrid();
  }
}
